(() => {
  const T = (key, fallback, n) => (window.stlukesT || ((k, f, n) => f.replace('{n}', n)))(key, fallback, n);
  if (!document.querySelector('[data-filter-panel]') || !document.querySelector('[data-filter-open]')) return;

  // Everything inside the section is looked up when it is used, not held from
  // page load: a department tile swaps the whole section in place (see "tiles
  // without a page load" below), and the nodes it replaces take their
  // listeners with them. bind() wires the new ones; the document level
  // listeners are added once and read the live DOM.
  const panel = () => document.querySelector('[data-filter-panel]');
  const openButton = () => document.querySelector('[data-filter-open]');
  const drops = () => [...document.querySelectorAll('[data-fdrop]')];

  const desktop = window.matchMedia('(min-width: 900px)');
  let scrim = null;

  const close = () => {
    delete panel()?.dataset.open;
    document.body.style.overflow = '';
    scrim?.remove();
    scrim = null;
    openButton()?.focus();
  };

  const open = () => {
    panel().dataset.open = 'true';
    document.body.style.overflow = 'hidden';
    scrim = document.createElement('button');
    scrim.className = 'col__scrim';
    scrim.setAttribute('aria-label', T('close_filters', 'Close filters'));
    scrim.addEventListener('click', close);
    document.body.appendChild(scrim);
    panel().querySelector('[data-filter-close]')?.focus();
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (panel()?.dataset.open) close();
    drops().forEach((drop) => { drop.open = false; });
  });

  // Leaving the drawer open while resizing to desktop would trap scrolling.
  desktop.addEventListener('change', (event) => {
    if (event.matches && panel()?.dataset.open) close();
  });

  // Clicking away closes any open filter dropdown.
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-fdrop]')) return;
    drops().forEach((drop) => { drop.open = false; });
  });

  // "Back to top" under the grid: up to the rails and filters, where the next
  // choice is made.
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-to-top]')) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: still ? 'auto' : 'smooth' });
  });

  const bind = () => {
    openButton().addEventListener('click', open);
    panel().querySelector('[data-filter-close]')?.addEventListener('click', close);

    // Sorting submits immediately; filters wait for Apply so shoppers can pick
    // several without a reload between each one.
    document.querySelector('[data-auto-submit]')?.addEventListener('change', (event) => {
      event.target.form.submit();
    });

    // Only one filter dropdown open at a time.
    drops().forEach((drop) => {
      drop.addEventListener('toggle', () => {
        if (!drop.open) return;
        drops().filter((other) => other !== drop).forEach((other) => { other.open = false; });
      });
    });

    bindBrandSearch();
    bindPaging();
  };

  // ---------- paging without a page load ----------

  // Only the grid changes between pages, so only the grid is fetched. A full
  // navigation throws away the header, menu, drawer and footer and rebuilds
  // them identically, which is the flash between pages.
  const sectionId = document.querySelector('.col')?.dataset.sectionId;
  const canSwap = Boolean(sectionId && 'fetch' in window && 'replaceState' in history);
  const pageCache = new Map();

  const render = (url) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    return fetch(target.href)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  };

  const grab = (url) => {
    if (pageCache.has(url)) return pageCache.get(url);
    const pending = render(url).then((doc) => {
      const fresh = doc.querySelector('[data-results]');
      if (!fresh) throw new Error('no results block in the response');
      return fresh.innerHTML;
    });
    pageCache.set(url, pending);
    return pending;
  };

  const settle = (results) => {
    // Land just under the sticky toolbar rather than at the very top, so the
    // first row is the first thing read.
    const bar = document.querySelector('[data-toolbar]');
    const offset = (bar?.getBoundingClientRect().height || 0) + (parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-h')
    ) || 96);

    const top = results.getBoundingClientRect().top + window.scrollY - offset - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  const showPage = async (url, push) => {
    const results = document.querySelector('[data-results]');
    results.dataset.busy = 'true';

    try {
      results.innerHTML = await grab(url);
    } catch (error) {
      // A page that cannot be fetched is still a page that can be visited.
      window.location.href = url;
      return;
    }

    delete results.dataset.busy;
    if (push) history.pushState({ paged: true }, '', url);
    settle(results);
    results.querySelector('.pcard a')?.focus({ preventScroll: true });
  };

  function bindPaging() {
    const results = document.querySelector('[data-results]');
    if (!results || !canSwap) return;

    // Delegated, because the pager is inside the block being replaced.
    results.addEventListener('click', (event) => {
      const link = event.target.closest('.pager__page, .pager__step');
      if (!link || link.tagName !== 'A') return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();
      showPage(link.href, true);
    });

    // Warm the next page while the shopper is still deciding.
    results.addEventListener('pointerenter', (event) => {
      const link = event.target.closest?.('.pager__page, .pager__step');
      if (link && link.tagName === 'A') grab(link.href).catch(() => {});
    }, true);
  }

  // ---------- department tiles without a page load ----------

  // A category or brand tile changes the products, the filter bar's state, the
  // chips and the heading, but not the rails the shopper is choosing from, so
  // the section is swapped in place and the page does not jump or flash. The
  // address still changes, so Back, sharing and a refresh all land on the
  // same choice. Scripts inside the new markup (rail arrows, grid density)
  // are re-run, since markup set through innerHTML never runs its own.
  const sectionCache = new Map();

  const grabSection = (url) => {
    if (sectionCache.has(url)) return sectionCache.get(url);
    const pending = render(url).then((doc) => {
      const fresh = doc.querySelector('.col');
      if (!fresh) throw new Error('no section in the response');
      return fresh.innerHTML;
    });
    sectionCache.set(url, pending);
    pending.catch(() => sectionCache.delete(url));
    return pending;
  };

  const swapSection = async (url, push) => {
    const root = document.querySelector('.col');
    root.dataset.busy = 'true';

    let html;
    try {
      html = await grabSection(url);
    } catch (error) {
      window.location.href = url;
      return;
    }

    const y = window.scrollY;
    if (scrim) close();
    root.innerHTML = html;
    root.querySelectorAll('script').forEach((old) => {
      const fresh = document.createElement('script');
      fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    });
    bind();
    window.scrollTo(0, y);
    delete root.dataset.busy;
    if (push) {
      history.pushState({ dept: true }, '', url);
      reveal(root);
    }
  };

  // After a tile is picked the new products usually sit below the fold (the
  // heading landed just off screen on desktop and phone), so the tap looked
  // like it did nothing. When the products are not already in view, glide
  // down until the heading naming the choice sits under the site header with
  // the first row beneath it. Never upward, and not on Back.
  const reveal = (root) => {
    const results = root.querySelector('[data-results]');
    if (!results || results.getBoundingClientRect().top < window.innerHeight * 0.7) return;

    const mark = root.querySelector('.dept__everything') || results;
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 96;
    const top = mark.getBoundingClientRect().top + window.scrollY - header - 12;
    if (top <= window.scrollY) return;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top, behavior: still ? 'auto' : 'smooth' });
  };

  if (canSwap) {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-dept-swap]');
      if (!link) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      swapSection(link.href, true);
    });

    // Warm a tile's result on hover, so the tap feels instant.
    document.addEventListener('pointerenter', (event) => {
      const link = event.target.closest?.('[data-dept-swap]');
      if (link) grabSection(link.href).catch(() => {});
    }, true);

    window.addEventListener('popstate', (event) => {
      if (event.state?.dept) swapSection(window.location.href, false);
      else if (event.state?.paged) showPage(window.location.href, false);
    });

    // So the first Back press returns to this page rather than leaving.
    history.replaceState({ paged: true, dept: Boolean(document.querySelector('[data-dept]')) }, '', window.location.href);
  }

  // Brand lists run past a hundred names in the big departments, so they are
  // typed down rather than scrolled. Filtering is local: every brand in the
  // collection is already in the DOM.
  function bindBrandSearch() {
    document.querySelectorAll('[data-brand-search]').forEach((input) => {
      const scope = input.closest('.fdrop__panel, .facet');
      const list = scope?.querySelector('[data-brand-list]');
      const empty = scope?.querySelector('[data-brand-empty]');
      if (!list) return;

      const items = [...list.querySelectorAll('[data-brand]')];

      const apply = () => {
        const query = input.value.trim().toLowerCase();
        let shown = 0;

        items.forEach((item) => {
          // Prefix first, then anywhere, so "la roche" finds the brand and
          // "roche" still does.
          const hit = query === '' || item.dataset.brand.includes(query);
          item.hidden = !hit;
          if (hit) shown += 1;
        });

        if (empty) empty.hidden = shown > 0;
      };

      input.addEventListener('input', apply);

      // Enter on a single remaining match goes straight there.
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const first = items.find((item) => !item.hidden);
        if (first) window.location.href = first.href;
      });
    });
  }

  bind();
})();
